import { Paper, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { theme } from '../../theme';
import Button from '../UI/Button';
import ClearIcon from '@mui/icons-material/Clear';
import LinkButton from '../UI/LinkButton';
import { useFileUpload } from '../../hooks/useFileUpload';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface FileUploadProps {
  allowedFileTypes: string[];
  onFileSelect?: (file: File | null) => void;
  className?: string;
  minHeight?: string | number;
}

export default function FileUpload({
  allowedFileTypes,
  onFileSelect,
  className,
  minHeight = '200px',
}: FileUploadProps) {
  const { t } = useTranslation();
  const {
    file,
    error,
    isDragging,
    fileInputRef,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleFileChange,
    handleRemoveFile,
  } = useFileUpload({ allowedFileTypes, onFileSelect });

  return (
    <Paper
      className={className}
      sx={{
        px: 2,
        py: 4,
        textAlign: 'center',
        border: `2px dashed`,
        borderColor: isDragging
          ? theme.palette.primary.main
          : file
            ? theme.palette.primary.main
            : 'grey.200',
        boxShadow: 'none',
        cursor: file ? 'default' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        bgcolor: isDragging ? 'rgba(0, 0, 0, 0.04)' : 'white',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: file ? 'inherit' : 'grey.100',
        },
        minHeight,
      }}
      onClick={(e) => {
        if (!file) {
          e.preventDefault();
          fileInputRef.current?.click();
        }
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      {isDragging ? (
        <Typography
          variant='h5'
          sx={{ fontWeight: 600, color: theme.palette.primary.main }}
        >
          {t('file-upload.drop-file')}
        </Typography>
      ) : !file ? (
        <>
          <Typography variant='h5' sx={{ fontWeight: 600 }}>
            {t('file-upload.select-file')}
          </Typography>
          <Typography variant='h6' sx={{ fontSize: '14px' }}>
            {t('file-upload.select-file-alt')}
          </Typography>
          <Button role={undefined} tabIndex={-1} id='fileInput'>
            {t('file-upload.browse')}
            <VisuallyHiddenInput
              type='file'
              ref={fileInputRef}
              onChange={handleFileChange}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </Button>
        </>
      ) : (
        <>
          <Typography variant='h6'>{file.name}</Typography>
          <LinkButton
            onClick={handleRemoveFile}
            style={{
              color: '#9b0000',
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              gap: 2,
            }}
          >
            <ClearIcon sx={{ fontSize: '16px' }} />
            {t('file-upload.remove-file')}
          </LinkButton>
        </>
      )}
      {error && <Typography color='error'>{error}</Typography>}
    </Paper>
  );
}
